import React, {
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  animate,
  AnimationPlaybackControls,
  MotionValue,
  useMotionValue,
  useReducedMotion,
  ValueAnimationTransition,
} from "motion/react";
import { SwipeFiltersContext } from "common/Filters/common/SwipeFilters.context.ts";
import {
  getPullDirection,
  getPullDistance,
  getRefreshFinishDelay,
  NEW_LOOK_PULL_MODEL,
  PullModel,
  shouldHoldTouch,
  shouldRefreshOnRelease,
} from "common/Scroll/pullToRefresh";

interface useNewLookScrollViewProps {
  onRefresh?: () => void;
  refreshing?: boolean;
  loading?: boolean;
}

interface PullGesture {
  startX: number;
  anchorY?: number;
  direction?: "pull" | "sideways";
}

interface RefreshRun {
  startedAt: number;
  sawRefreshing: boolean;
}

type RefreshMotion = ReturnType<typeof useRefreshMotion>;

interface RefreshRunProps {
  onRefresh?: () => void;
  refreshing?: boolean;
  model: PullModel;
  motion: RefreshMotion;
}

const REDUCED_MOTION_TRANSITION = { duration: 0.12, ease: "easeOut" } as const;
const SHRINK_TRANSITION = {
  duration: 0.2,
  ease: [0.4, 0, 1, 1],
} as const;

const useRefreshMotion = (model: PullModel) => {
  const reducedMotion = useReducedMotion();
  const distance = useMotionValue(0);
  const finish = useMotionValue(0);
  const animationRef = useRef<AnimationPlaybackControls | undefined>(undefined);

  const run = (
    value: MotionValue<number>,
    target: number,
    transition: ValueAnimationTransition<number>,
    onComplete?: () => void
  ) => {
    animationRef.current?.stop();
    const controls = animate(value, target, transition);
    animationRef.current = controls;
    controls.then(() => {
      if (animationRef.current === controls) onComplete?.();
    });
  };

  const settle = (target: number, onComplete?: () => void) =>
    run(
      distance,
      target,
      reducedMotion
        ? REDUCED_MOTION_TRANSITION
        : {
            type: "spring",
            stiffness: model.stiffness,
            damping: model.damping,
          },
      onComplete
    );

  const shrink = (onComplete: () => void) =>
    run(
      finish,
      1,
      reducedMotion ? REDUCED_MOTION_TRANSITION : SHRINK_TRANSITION,
      () => {
        distance.set(0);
        finish.set(0);
        onComplete();
      }
    );

  const follow = (value: number) => {
    animationRef.current?.stop();
    animationRef.current = undefined;
    distance.set(value);
  };

  useEffect(() => () => animationRef.current?.stop(), []);

  return { distance, finish, settle, shrink, follow };
};

const useSwipeOverflowLock = (scrollRef: RefObject<HTMLDivElement | null>) => {
  const hiddenRef = useRef(false);

  const hide = () => {
    if (!scrollRef.current || hiddenRef.current) return;
    scrollRef.current.style.overflow = "hidden";
    hiddenRef.current = true;
  };

  const restore = () => {
    if (!scrollRef.current || !hiddenRef.current) return;
    scrollRef.current.style.overflow = "";
    hiddenRef.current = false;
  };

  return { hide, restore };
};

const useRefreshRun = ({
  onRefresh,
  refreshing,
  model,
  motion,
}: RefreshRunProps) => {
  const runRef = useRef<RefreshRun | undefined>(undefined);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startRefresh = (requested: boolean) => {
    runRef.current = {
      startedAt: performance.now(),
      sawRefreshing: !!refreshing,
    };
    setIsRefreshing(true);
    motion.settle(model.loadingDistance);
    if (!requested) return;
    try {
      onRefresh?.();
    } catch {
      return;
    }
  };

  const finishRefresh = () => {
    runRef.current = undefined;
    const done = () => setIsRefreshing(false);
    if (model.shrinksOnFinish) motion.shrink(done);
    else motion.settle(0, done);
  };

  const latestRef = useRef({ startRefresh, finishRefresh });
  latestRef.current = { startRefresh, finishRefresh };

  useEffect(() => {
    const run = runRef.current;
    if (!isRefreshing || !run) return;
    if (refreshing) {
      run.sawRefreshing = true;
      return;
    }
    const delay = getRefreshFinishDelay(
      performance.now() - run.startedAt,
      run.sawRefreshing
    );
    const timeout = window.setTimeout(
      () => latestRef.current.finishRefresh(),
      delay
    );
    return () => window.clearTimeout(timeout);
  }, [isRefreshing, refreshing]);

  return { isRefreshing, startRefresh };
};

const useTouchHold = (
  scrollRef: RefObject<HTMLDivElement | null>,
  gestureRef: RefObject<PullGesture | undefined>,
  enabled: boolean
) => {
  const { swipeActiveRef } = useContext(SwipeFiltersContext) || {};

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !enabled) return;
    const holdTouch = (event: TouchEvent) => {
      const gesture = gestureRef.current;
      const touch = event.touches[0];
      if (!gesture || gesture.anchorY === undefined || !touch) return;
      if (!event.cancelable || swipeActiveRef?.current) return;
      if (element.scrollTop > 0) return;
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.anchorY;
      if (shouldHoldTouch(gesture.direction, deltaX, deltaY)) {
        event.preventDefault();
      }
    };
    element.addEventListener("touchmove", holdTouch, { passive: false });
    return () => element.removeEventListener("touchmove", holdTouch);
  }, [scrollRef, gestureRef, enabled, swipeActiveRef]);
};

const useNewLookScrollView = ({
  onRefresh,
  refreshing,
  loading,
}: useNewLookScrollViewProps) => {
  const { swipeActiveRef } = useContext(SwipeFiltersContext) || {};
  const model = NEW_LOOK_PULL_MODEL;
  const scrollRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<PullGesture | undefined>(undefined);
  const motion = useRefreshMotion(model);
  const overflowLock = useSwipeOverflowLock(scrollRef);
  const { isRefreshing, startRefresh } = useRefreshRun({
    onRefresh,
    refreshing,
    model,
    motion,
  });
  useTouchHold(scrollRef, gestureRef, !!onRefresh);

  const isAtTop = () => (scrollRef.current?.scrollTop ?? 0) <= 0;

  const abandonPull = () => {
    if (gestureRef.current?.direction === "pull") motion.settle(0);
    gestureRef.current = undefined;
  };

  const releasePull = (canRefresh: boolean) => {
    const gesture = gestureRef.current;
    gestureRef.current = undefined;
    if (gesture?.direction !== "pull" || isRefreshing) return;
    if (canRefresh && shouldRefreshOnRelease(motion.distance.get(), model)) {
      startRefresh(true);
    } else {
      motion.settle(0);
    }
  };

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    abandonPull();
    const isSingleTouch = event.touches.length === 1;
    if (!isSingleTouch || !onRefresh || loading || isRefreshing) return;
    if (!scrollRef.current) return;
    const touch = event.touches[0];
    scrollRef.current.style.overscrollBehaviorY = isAtTop()
      ? "none"
      : "contain";
    gestureRef.current = {
      startX: touch.clientX,
      anchorY: isAtTop() ? touch.clientY : undefined,
    };
  };

  const lockDirection = (
    gesture: PullGesture,
    deltaX: number,
    deltaY: number
  ) => {
    const direction = getPullDirection(deltaX, deltaY);
    if (direction === "up") gesture.anchorY = undefined;
    else gesture.direction = direction;
  };

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (swipeActiveRef?.current) {
      overflowLock.hide();
      return abandonPull();
    }
    const gesture = gestureRef.current;
    const touch = event.touches[0];
    if (!gesture || !touch || isRefreshing) return;
    if (gesture.direction === "sideways") return;
    if (gesture.anchorY === undefined) {
      if (!isAtTop()) return;
      gesture.anchorY = touch.clientY;
      gesture.startX = touch.clientX;
    }
    const deltaY = touch.clientY - gesture.anchorY;
    if (!gesture.direction) {
      lockDirection(gesture, touch.clientX - gesture.startX, deltaY);
    }
    if (gesture.direction !== "pull") return;
    motion.follow(isAtTop() ? getPullDistance(deltaY, model) : 0);
  };

  const onTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length) return;
    overflowLock.restore();
    releasePull(true);
  };

  const onTouchCancel = () => {
    overflowLock.restore();
    releasePull(false);
  };

  return {
    scrollRef,
    scrollViewProps: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
    spinnerProps: {
      distance: motion.distance,
      finish: motion.finish,
      isRefreshing,
      model,
    },
    showSpinner: !!onRefresh,
  };
};

export default useNewLookScrollView;
